<script setup lang="ts">
import PreregistrationAccessCard from '~/modules/public/preregistration/presentation/PreregistrationAccessCard.vue'
import { publicPreregistrationRepository } from '~/modules/public/preregistration/infrastructure/preregistration.repository'
import { createPreregistrationUseCases } from '~/modules/public/preregistration/application/preregistration.use-cases'
import type { PublicPreregistration } from '~/shared-kernel/domain/preregistration'
import PublicEventMediaPlayer from '~/modules/public/purchase/presentation/PublicEventMediaPlayer.vue'
import PublicPurchaseEventHelp from '~/modules/public/purchase/presentation/PublicPurchaseEventHelp.vue'
import SiteTopbar from '~/shared-kernel/presentation/navigation/SiteTopbar.vue'
import { publicCartDisplayLines } from '~/modules/public/purchase/presentation/public-cart-display'
import { publicLocalityPriceCaptions } from '~/modules/public/purchase/presentation/public-locality-captions'
import PublicPurchaseCartLines from '~/modules/public/purchase/presentation/PublicPurchaseCartLines.vue'
import PublicPurchaseCartSummary from '~/modules/public/purchase/presentation/PublicPurchaseCartSummary.vue'
import PublicPurchasePromotionForm from '~/modules/public/purchase/presentation/PublicPurchasePromotionForm.vue'
import PublicPurchaseQuantities from '~/modules/public/purchase/presentation/PublicPurchaseQuantities.vue'
import PublicPurchaseBar from '~/modules/public/purchase/presentation/PublicPurchaseBar.vue'
import type { EventMapExportSnapshot } from '~/shared-kernel/presentation/event-map/event-map-export.types'
import RealtimeEventMapRenderer from '~/shared-kernel/presentation/event-map/RealtimeEventMapRenderer.vue'
import type { PublicCartCandidate, PublicEvent, PublicPurchaseProjection } from '~/modules/public/purchase/domain/public-purchase.types'
import { formatBusinessDate } from '~/shared-kernel/domain/business-time'
import type { PublicPurchaseMap } from '~/modules/public/purchase/application/public-purchase-map.types'
import type { PublicHeldCart, PublicHeldCartEvent } from '~/modules/public/purchase/domain/public-purchase-session.types'
import { createPublicPurchaseRepositories } from '~/modules/public/purchase/infrastructure/public-purchase.repository'
import { createInitialPublicUnitCandidates, createPublicCartCandidate, publicPriceBreakdown } from '~/modules/public/purchase/domain/public-purchase'
import { publicEventArtwork } from '~/modules/public/events/public-event-discovery'
import { publicImageAttributes } from '~/modules/public/purchase/presentation/media/public-image'
import { useAbsoluteUrl, useJsonLd, usePageSeo } from '~/shared-kernel/presentation/composables/usePageSeo'
import { usePublicPurchaseSession } from '~/modules/public/purchase/presentation/public-purchase-session.store'
import { resolveAppErrorMessage } from '~/shared-kernel/presentation/composables/useAppErrorMessage'
import { useTqToast } from '~/shared-kernel/presentation/composables/useTqToast'
import { useCurrencyFormatter } from '~/shared-kernel/presentation/composables/useCurrencyFormatter'
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import type { InventoryMapPatch } from '~/shared-kernel/domain/inventory-map-patch'
import type { DriveStep, Driver } from 'driver.js'

const props = defineProps<{ slug: string }>()
const { t, locale } = useI18n()
const router = useRouter()
const localePath = useLocalePath()
const eventsPath = computed(() => localePath('/events'))

// Coming from the catalog, history back restores its filters and scroll; direct visits get the catalog link.
function backToEvents(event: MouseEvent) {
  const previous = window.history.state?.back
  if (typeof previous !== 'string' || !previous.startsWith(eventsPath.value)) return
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return
  event.preventDefault()
  router.back()
}
const repositories = createPublicPurchaseRepositories()
const preregistration = createPreregistrationUseCases(publicPreregistrationRepository)
const priorityAccess = shallowRef<PublicPreregistration | null>(null)
let priorityTimer: ReturnType<typeof setTimeout> | undefined
let loadGeneration = 0
const purchaseSession = usePublicPurchaseSession()
const toast = useTqToast()
const { formatMoney } = useCurrencyFormatter()
const promotionalAudio = shallowRef<string | null>(null)
const pulepCode = shallowRef<string | null>(null)
const organizers = shallowRef<string[]>([])
const event = shallowRef<PublicEvent | null>(null)
const purchaseMap = shallowRef<PublicPurchaseMap | null>(null)
const focusedLocalityId = shallowRef<string | null>(null)
const inspectedUnitId = shallowRef<string | null>(null)
const selectedLines = shallowRef<PublicCartCandidate[]>([])
const loading = shallowRef(true)
const hydrated = shallowRef(false)
const error = shallowRef<unknown>(null)
// Crawlers, link previews and the first paint get the event from the server; hydration reuses the payload.
// Client navigations skip it so `load()` keeps reading the event and its access in parallel.
const { data: initialEvent, error: initialEventError } = await useAsyncData(
  `public-event:${props.slug}`,
  () => repositories.catalog.getBySlug(props.slug),
  { immediate: import.meta.server }
)
event.value = initialEvent.value ?? null
let prefetchedEvent = event.value
if (import.meta.server) {
  if (event.value?.status === 'preregistration') {
    await navigateTo(localePath(`/events/${event.value.slug}/preregister`), { redirectCode: 302 })
  } else if (!event.value && !initialEventError.value) {
    setResponseStatus(useRequestEvent()!, 404)
  }
}
const absoluteUrl = useAbsoluteUrl()
const eventStartsAt = computed(() => event.value
  ? formatBusinessDate(event.value.occurrence.startsAt, locale.value, { dateStyle: 'long', timeStyle: 'short' })
  : '')
const unavailableReason = computed<'soldOut' | 'salesClosed' | null>(() => {
  const current = event.value
  if (!current) return null
  if (current.status === 'sold_out') return 'soldOut'
  const endsAt = Date.parse(current.salesEndDate ?? '')
  return Number.isFinite(endsAt) && endsAt < Date.now() ? 'salesClosed' : null
})
// The pre-sale card and the unavailable state replace the whole purchase view, header included.
const showEventHeader = computed(() => !error.value && !(priorityAccess.value && !priorityAccess.value.access.canPurchase))
const eventShareImage = computed(() => {
  const artwork = event.value && publicEventArtwork(event.value)
  return artwork ? publicImageAttributes(artwork, '').src : null
})
usePageSeo({
  title: () => event.value ? t('app.meta.eventTitle', { name: event.value.name }) : t('public.purchase.unavailableTitle'),
  description: () => event.value
    ? t('app.meta.eventDescription', { name: event.value.name, when: [event.value.venue?.name, eventStartsAt.value].filter(Boolean).join(', ') })
    : t('app.meta.description'),
  image: eventShareImage
})
useJsonLd(() => {
  const current = event.value
  if (!current) return null
  const url = absoluteUrl(localePath(`/events/${current.slug}`))
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: current.name,
    url,
    startDate: current.occurrence.startsAt,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(current.venue
      ? { location: { '@type': 'Place', name: current.venue.name, address: current.venue.address || current.venue.name } }
      : {}),
    ...(eventShareImage.value ? { image: [absoluteUrl(eventShareImage.value)] } : {}),
    organizer: { '@type': 'Organization', name: t('public.brand'), url: absoluteUrl('/') },
    offers: {
      '@type': 'Offer',
      url,
      availability: current.status === 'sold_out' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      ...(current.salesStartDate ? { validFrom: current.salesStartDate } : {})
    }
  }
})
const promotionLoading = shallowRef(false)
let availabilityGeneration = 0
let expiryTimer: ReturnType<typeof setTimeout> | null = null
let mounted = false
let purchaseTour: Driver | null = null
let purchaseTourAwaitingSeat = false
let purchaseTourAwaitingLocality = false
let purchaseTourAwaitingCart = false
// While the tour runs, the sheet must not trap focus or treat popover clicks as outside presses.
const purchaseTouring = shallowRef(false)
// Below this width the cart no longer fits beside the map: it moves to a bottom sheet behind a fixed bar.
const compact = shallowRef(false)
let compactMedia: MediaQueryList | null = null
const cartOpen = shallowRef(false)
watch(cartOpen, (open) => {
  if (!open || !purchaseTourAwaitingCart) return
  purchaseTourAwaitingCart = false
  // Let the sheet settle so the next steps highlight its final position.
  setTimeout(() => purchaseTour?.moveNext(), 350)
})
const addedNotice = shallowRef<string | null>(null)
let noticeTimer: ReturnType<typeof setTimeout> | null = null

function syncCompact() {
  compact.value = Boolean(compactMedia?.matches)
}

function showAddedNotice(label: string) {
  if (noticeTimer) clearTimeout(noticeTimer)
  addedNotice.value = t('public.purchase.added', { label })
  noticeTimer = setTimeout(() => {
    addedNotice.value = null
    noticeTimer = null
  }, 2_500)
}

function hasAvailableSeats() {
  const document = purchaseMap.value?.renderDocument
  if (!document) return false
  return document.localities.some(locality => (
    locality.available !== false
    && document.sections.some(section => (
      section.localityId === locality.id
      && document.seatLayouts[section.id]?.units.some(unit => (
        unit.kind === 'seat'
        && unit.available !== false
        && !selectedLine(unit.id)
      ))
    ))
  ))
}

async function startPurchaseTour() {
  const { driver } = await import('driver.js')
  await import('driver.js/dist/driver.css')
  purchaseTour?.destroy()

  const step = (key: string, target: string | null): DriveStep => ({
    ...(target ? {
      element: `[data-purchase-tour="${target}"]`,
      waitForElement: target === 'reservation' ? 5_000 : 1_000
    } : {}),
    popover: {
      title: t(`public.purchase.eventHelp.steps.${key}.title`),
      description: t(`public.purchase.eventHelp.steps.${key}.body`)
    }
  })
  const present = (target: string) => document.querySelector(`[data-purchase-tour="${target}"]`) ? target : null
  // On phones the cart lives in a sheet behind the bar's button; the tour opens it before pointing inside it.
  const target = (name: string) => compact.value && name === 'cart' ? 'view-cart' : name
  const cartStep = step('cart', target('cart'))
  if (compact.value) {
    Object.assign(cartStep, {
      disableActiveInteraction: false,
      onHighlighted: () => { purchaseTourAwaitingCart = true },
      onDeselected: () => { purchaseTourAwaitingCart = false },
      popover: { ...cartStep.popover, onNextClick: () => {
        if (cartOpen.value) purchaseTour?.moveNext()
        else cartOpen.value = true
      } }
    })
  }
  const selectionSteps: DriveStep[] = hasAvailableSeats()
    ? [
        {
          element: '.event-map-renderer__localities',
          waitForElement: 1_000,
          disableActiveInteraction: false,
          onHighlighted: () => { purchaseTourAwaitingLocality = true },
          onDeselected: () => { purchaseTourAwaitingLocality = false },
          popover: {
            title: t('public.purchase.eventHelp.tourSteps.locality.title'),
            description: t('public.purchase.eventHelp.tourSteps.locality.body'),
            showButtons: ['close', 'previous']
          }
        },
        {
          // General admission has no seats: point at its stepper (docked above the bar on phones) instead.
          element: () => document.querySelector(focusedGeneralAdmission.value.length
            ? `[data-purchase-tour="${compact.value ? 'cart-bar' : 'quantity'}"]`
            : '[data-purchase-tour="map"]') ?? document.body,
          waitForElement: 1_000,
          disableActiveInteraction: false,
          onHighlighted: () => { purchaseTourAwaitingSeat = true },
          onDeselected: () => { purchaseTourAwaitingSeat = false },
          popover: {
            title: t('public.purchase.eventHelp.tourSteps.seat.title'),
            description: t('public.purchase.eventHelp.tourSteps.seat.body'),
            showButtons: ['close', 'previous']
          }
        }
      ]
    : [step('quantity', present('quantity'))]
  const steps: DriveStep[] = [
    step('choose', target('event')),
    { ...step('map', target('map')), disableActiveInteraction: false },
    ...selectionSteps,
    cartStep,
    step('reservation', target('reservation')),
    step('promotion', target('promotion')),
    step('checkout', target('checkout')),
    step('tickets', null),
    step('troubleshooting', null)
  ]
  for (const [index, tourStep] of steps.entries()) {
    if (!tourStep.popover?.title) continue
    tourStep.popover.title = `${index + 1}. ${tourStep.popover.title.replace(/^\d+\.\s*/, '')}`
  }
  purchaseTourAwaitingSeat = false
  purchaseTourAwaitingLocality = false
  purchaseTourAwaitingCart = false
  purchaseTouring.value = true
  const activeTour = driver({
    steps,
    // Animated transitions between the map and its locality toolbar leave stale interaction locks.
    animate: false,
    smoothScroll: true,
    allowClose: true,
    allowScroll: true,
    allowKeyboardControl: true,
    disableActiveInteraction: true,
    showProgress: true,
    progressText: '{{current}} / {{total}}',
    nextBtnText: t('public.purchase.eventHelp.next'),
    prevBtnText: t('public.purchase.eventHelp.previous'),
    doneBtnText: t('public.purchase.eventHelp.tourDone'),
    popoverClass: 'tq-purchase-tour',
    stagePadding: 8,
    stageRadius: 8,
    onPopoverRender: (popover) => {
      popover.closeButton.setAttribute('aria-label', t('actions.close'))
      popover.closeButton.setAttribute('title', t('actions.close'))
    },
    onDestroyed: () => {
      purchaseTourAwaitingSeat = false
      purchaseTourAwaitingLocality = false
      purchaseTourAwaitingCart = false
      purchaseTouring.value = false
      if (purchaseTour === activeTour) purchaseTour = null
    }
  })
  purchaseTour = activeTour
  activeTour.drive()
}

function isStructurallyAcceptedEvent(
  cart: PublicHeldCart | null,
  cartEvent: PublicHeldCartEvent | undefined
) {
  return Boolean(
    cart
    && cartEvent
    && ['held', 'checking_out'].includes(cart.phase)
    && ['held', 'renewing'].includes(cartEvent.status)
  )
}

function isActiveAcceptedEvent(
  cart: PublicHeldCart | null,
  cartEvent: PublicHeldCartEvent | undefined
) {
  if (!isStructurallyAcceptedEvent(cart, cartEvent) || !cartEvent) return false
  const now = Date.now()
  return Date.parse(cartEvent.expiresAt) > now
    && Date.parse(cartEvent.hardExpiresAt) > now
}

function restoreAcceptedSelection() {
  const projection = purchaseMap.value?.projection
  const loadedEvent = event.value
  if (!projection || !loadedEvent) return
  const cart = purchaseSession.acceptedCart
  const heldEvent = cart?.events.find(item => item.event.id === loadedEvent.id)
  selectedLines.value = (isActiveAcceptedEvent(cart, heldEvent) ? heldEvent?.lines : [])?.map(line => ({
    ...line,
    localityId: line.localityId,
    ...(line.unitId ? { unitId: line.unitId } : {}),
    quantity: line.quantity,
    price: line.price,
    ...(line.lineLabel ? { lineLabel: line.lineLabel } : {}),
    ...(line.unitLabel ? { unitLabel: line.unitLabel } : {}),
    ...(line.unitNumber ? { unitNumber: line.unitNumber } : {}),
    ...(line.unitCapacity !== undefined ? { unitCapacity: line.unitCapacity } : {})
  })) ?? []
}

function lineKey(line: Pick<PublicCartCandidate, 'localityId' | 'unitId'>) {
  return line.unitId ?? `locality:${line.localityId}`
}

function selectionSignature(lines: PublicCartCandidate[]) {
  return lines
    .map(line => `${lineKey(line)}:${line.quantity}`)
    .sort()
    .join('|')
}

function remainsAvailable(line: PublicCartCandidate, projection: PublicPurchaseProjection) {
  if (line.unitId) {
    return projection.units.some(unit => unit.id === line.unitId && unit.available)
  }
  const locality = projection.localities.find(item => item.id === line.localityId)
  return Boolean(locality && locality.availableQuantity >= line.quantity)
}

async function refreshAvailability(patchedMap?: PublicPurchaseMap) {
  const loadedEvent = event.value
  if (!loadedEvent) return
  const generation = ++availabilityGeneration
  const contextKey = `${loadedEvent.id}:${loadedEvent.occurrence.id}`
  const previousSelection = selectedLines.value
  const previousCart = purchaseSession.acceptedCart
  const previousCartEvent = previousCart?.events.find(
    item => item.event.id === loadedEvent.id && item.occurrence.id === loadedEvent.occurrence.id
  )
  const previousAcceptedKeys = new Set(
    isStructurallyAcceptedEvent(previousCart, previousCartEvent)
      ? (previousCartEvent?.lines ?? []).map(lineKey)
      : []
  )
  const mapRequest = patchedMap
    ? Promise.resolve(patchedMap)
    : repositories.purchaseMap.get(
        loadedEvent.slug,
        loadedEvent.occurrence.id
      )
  const sessionRequest = purchaseSession.refresh().then(
    () => true,
    () => false
  )
  const nextMap = await mapRequest
  if (
    generation !== availabilityGeneration
    || `${event.value?.id}:${event.value?.occurrence.id}` !== contextKey
  ) return
  purchaseMap.value = nextMap
  const sessionSucceeded = await sessionRequest
  if (
    generation !== availabilityGeneration
    || `${event.value?.id}:${event.value?.occurrence.id}` !== contextKey
  ) return
  if (!sessionSucceeded) return
  const cart = purchaseSession.acceptedCart
  const cartEvent = cart?.events.find(
    item => item.event.id === loadedEvent.id && item.occurrence.id === loadedEvent.occurrence.id
  )
  const sourceAcceptedLines = cartEvent?.lines ?? []
  const activeAccepted = isActiveAcceptedEvent(cart, cartEvent)
  const acceptedLines = (activeAccepted ? sourceAcceptedLines : []).map(line => ({
    ...line,
    localityId: line.localityId,
    ...(line.unitId ? { unitId: line.unitId } : {}),
    quantity: line.quantity,
    price: line.price,
    ...(line.lineLabel ? { lineLabel: line.lineLabel } : {}),
    ...(line.unitLabel ? { unitLabel: line.unitLabel } : {}),
    ...(line.unitNumber ? { unitNumber: line.unitNumber } : {}),
    ...(line.unitCapacity !== undefined ? { unitCapacity: line.unitCapacity } : {})
  }))
  const acceptedKeys = new Set(acceptedLines.map(lineKey))
  const terminalKeys = new Set(activeAccepted
    ? []
    : [...sourceAcceptedLines.map(lineKey), ...previousAcceptedKeys])
  const nextSelection = [
    ...acceptedLines,
    ...previousSelection.filter(line => (
      !acceptedKeys.has(lineKey(line))
      && !terminalKeys.has(lineKey(line))
      && remainsAvailable(line, nextMap.projection)
    ))
  ]
  selectedLines.value = nextSelection
  const nextSignature = selectionSignature(nextSelection)
  const desiredSignature = selectionSignature(purchaseSession.desiredSelection)
  if (
    nextSignature !== desiredSignature
    && (!activeAccepted || nextSignature !== selectionSignature(acceptedLines))
  ) {
    synchronize()
  }
}

function patchTouchesCart(previous: PublicPurchaseProjection, next: PublicPurchaseProjection) {
  const lines = [...selectedLines.value, ...(acceptedEvent.value?.lines ?? [])]
  if (!lines.length) return false
  const previousUnits = new Map(previous.units.map(unit => [unit.id, unit]))
  const nextUnits = new Map(next.units.map(unit => [unit.id, unit]))
  const previousLocalities = new Map(previous.localities.map(locality => [locality.id, locality]))
  const nextLocalities = new Map(next.localities.map(locality => [locality.id, locality]))
  return lines.some((line) => {
    if (line.unitId) {
      const before = previousUnits.get(line.unitId)
      const after = nextUnits.get(line.unitId)
      return before?.available !== after?.available || before?.availableCapacity !== after?.availableCapacity
    }
    return previousLocalities.get(line.localityId)?.availableQuantity !== nextLocalities.get(line.localityId)?.availableQuantity
  })
}

// Realtime patches only touch the map. The session is unaffected by other buyers,
// so it is refreshed only when the patch changes something this buyer holds or selected.
async function applyMapPatch(patch: InventoryMapPatch) {
  const loadedEvent = event.value
  const previous = purchaseMap.value
  if (!loadedEvent || !previous) return false
  const next = repositories.purchaseMap.applyPatch(loadedEvent.slug, patch)
  if (!next) return false
  if (patchTouchesCart(previous.projection, next.projection)) {
    await refreshAvailability(next)
    return true
  }
  purchaseMap.value = next
  return true
}

function scheduleExpiryRefresh() {
  if (expiryTimer) clearTimeout(expiryTimer)
  expiryTimer = null
  if (!import.meta.client) return
  const now = Date.now()
  const next = Math.min(...(purchaseSession.acceptedCart?.events ?? [])
    .map(item => Date.parse(item.expiresAt))
    .filter(deadline => deadline > now))
  if (Number.isFinite(next)) {
    expiryTimer = setTimeout(() => {
      expiryTimer = null
      void reconcileExpired()
    }, next - now + 1)
  }
}

async function reconcileExpired() {
  try {
    await refreshAvailability()
  } catch {
    // Keep the last accepted state until an authoritative read succeeds.
  }
  if (!mounted || document.visibilityState !== 'visible'
    || !purchaseSession.acceptedCart?.events.some(item => Date.parse(item.expiresAt) <= Date.now())) return
  if (expiryTimer) clearTimeout(expiryTimer)
  expiryTimer = setTimeout(() => {
    expiryTimer = null
    void reconcileExpired()
  }, 5_000)
}

function refreshWhenVisible() {
  if (document.visibilityState === 'visible') {
    if (priorityAccess.value && !priorityAccess.value.access.canPurchase) void load()
    else void reconcileExpired()
  }
}

watch(() => purchaseSession.acceptedCart?.events.map(item => item.expiresAt).join('|'), scheduleExpiryRefresh)

const mapRealtimeContext = computed(() => event.value
  ? { mode: 'public' as const, publicSlug: event.value.slug }
  : null)

async function load() {
  const generation = ++loadGeneration
  const isCurrent = () => mounted && generation === loadGeneration
  if (priorityTimer) clearTimeout(priorityTimer)
  priorityTimer = undefined
  loading.value = true
  error.value = null
  try {
    // Access depends only on the slug: start it with the event and keep catalog errors authoritative.
    const accessRequest = preregistration.get(props.slug)
    accessRequest.catch(() => undefined)
    // The server-rendered event serves the first load only; refreshes always ask the API again.
    const loadedEvent = prefetchedEvent ?? await repositories.catalog.getBySlug(props.slug)
    prefetchedEvent = null
    if (!isCurrent()) return
    if (!loadedEvent) throw Object.assign(new Error('PUBLIC_EVENT_NOT_FOUND'), { status: 404 })
    event.value = loadedEvent
    if (loadedEvent.status === 'preregistration') {
      purchaseMap.value = null
      await navigateTo(localePath(`/events/${loadedEvent.slug}/preregister`), { replace: true })
      return
    }
    // Listed but not purchasable (sold out or sales window over): show the state instead of requesting the map.
    if (unavailableReason.value) {
      purchaseMap.value = null
      priorityAccess.value = null
      return
    }
    const access = await accessRequest
    if (!isCurrent()) return
    priorityAccess.value = access
    if (!priorityAccess.value.access.canPurchase) {
      purchaseMap.value = null
      const next = priorityAccess.value.access.state === 'before_sale' ? priorityAccess.value.access.saleStartsAt : priorityAccess.value.access.publicStartsAt
      if (next) priorityTimer = setTimeout(() => { void load() }, Math.min(Math.max(Date.parse(next) - Date.now() + 250, 1000), 2_147_483_647))
      return
    }
    const [loadedMap] = await Promise.all([
      repositories.purchaseMap.get(loadedEvent.slug, loadedEvent.occurrence.id),
      purchaseSession.refresh()
    ])
    if (!isCurrent()) return
    event.value = loadedEvent
    purchaseMap.value = loadedMap
    restoreAcceptedSelection()
  } catch (requestError) {
    if (isCurrent()) error.value = requestError
  } finally {
    if (isCurrent()) loading.value = false
  }
}

onMounted(() => {
  mounted = true
  compactMedia = window.matchMedia('(max-width: 900px)')
  syncCompact()
  compactMedia.addEventListener('change', syncCompact)
  hydrated.value = true
  void load()
  scheduleExpiryRefresh()
  document.addEventListener('visibilitychange', refreshWhenVisible)
  window.addEventListener('pageshow', refreshWhenVisible)
})

onBeforeUnmount(() => {
  mounted = false
  ++loadGeneration
  if (priorityTimer) clearTimeout(priorityTimer)
  if (expiryTimer) clearTimeout(expiryTimer)
  purchaseTour?.destroy()
  document.removeEventListener('visibilitychange', refreshWhenVisible)
  window.removeEventListener('pageshow', refreshWhenVisible)
  compactMedia?.removeEventListener('change', syncCompact)
  if (noticeTimer) clearTimeout(noticeTimer)
})
const synchronizing = computed(() => purchaseSession.synchronizing)
const sessionError = computed(() => purchaseSession.error)
const acceptedEvent = computed(() => {
  const loadedEvent = event.value
  if (!loadedEvent) return undefined
  return purchaseSession.acceptedCart?.events.find(item => (
    item.event.id === loadedEvent.id && item.occurrence.id === loadedEvent.occurrence.id
  ))
})
const acceptedUnitIds = computed(() => (
  isActiveAcceptedEvent(purchaseSession.acceptedCart, acceptedEvent.value)
    ? (acceptedEvent.value?.lines ?? []).flatMap(line => line.unitId ? [line.unitId] : [])
    : []
))
const acceptedLocalityIds = computed(() => (
  isActiveAcceptedEvent(purchaseSession.acceptedCart, acceptedEvent.value)
    ? (acceptedEvent.value?.lines ?? []).flatMap(line => line.unitId ? [] : [line.localityId])
    : []
))
const pendingUnitIds = computed(() => {
  const accepted = new Set(acceptedUnitIds.value)
  return selectedLines.value.flatMap(line => (
    line.unitId && !accepted.has(line.unitId) ? [line.unitId] : []
  ))
})
const pendingLocalityIds = computed(() => {
  const accepted = new Set(acceptedLocalityIds.value)
  return selectedLines.value.flatMap(line => (
    !line.unitId && !accepted.has(line.localityId) ? [line.localityId] : []
  ))
})
const unitDetails = computed(() => Object.fromEntries(
  (purchaseMap.value?.projection.units ?? []).map(unit => [unit.id, {
    price: unit.price,
    priceBreakdown: publicPriceBreakdown(unit.price),
    capacity: unit.capacity,
    availableChairs: unit.isAtomic === false ? unit.availableCapacity : undefined
  }])
))
async function loadExportSnapshot(): Promise<EventMapExportSnapshot> {
  const current = event.value
  if (!current) throw new Error('Event unavailable')
  const map = await repositories.purchaseMap.get(current.slug, current.occurrence.id)
  return {
    document: map.renderDocument,
    eventName: current.name,
    capturedAt: map.inventoryAsOf ?? new Date().toISOString(),
    unitDetails: Object.fromEntries(map.projection.units.map(unit => [unit.id, {
      capacity: unit.capacity,
      availableChairs: unit.isAtomic === false ? unit.availableCapacity : (unit.available ? (unit.capacity ?? 1) : 0)
    }])),
    localityDetails: Object.fromEntries(map.projection.localities.map(locality => [locality.id, {
      availableChairs: locality.availableQuantity
    }]))
  }
}

const generalAdmissionLocalities = computed(() => (
  purchaseMap.value?.projection.localities.filter(locality => locality.saleMode === 'general-admission') ?? []
))
// General admission has no seats to tap; on narrow screens its stepper docks above the bar once the locality is chosen.
const focusedGeneralAdmission = computed(() => generalAdmissionLocalities.value.filter(locality => locality.id === focusedLocalityId.value))
const cartDisplayEvents = computed(() => {
  const projection = purchaseMap.value?.projection
  const loadedEvent = event.value
  const cart = purchaseSession.acceptedCart
  return (cart?.events ?? [])
    .filter(item => isActiveAcceptedEvent(cart, item))
    .map(item => ({
      key: `${item.event.id}:${item.occurrence.id}`,
      eventId: item.event.id,
      occurrenceId: item.occurrence.id,
      name: item.event.name,
      expiresAt: item.expiresAt,
      renewalAvailable: item.renewalAvailable,
      lines: publicCartDisplayLines(
        item,
        t,
        item.event.id === loadedEvent?.id && item.occurrence.id === loadedEvent.occurrence.id
          ? projection
          : undefined
      )
    }))
})
const generalAdmissionQuantities = computed(() => Object.fromEntries(
  selectedLines.value.flatMap(line => line.unitId ? [] : [[line.localityId, line.quantity]])
))
const localityCaptions = computed(() => purchaseMap.value
  ? publicLocalityPriceCaptions(purchaseMap.value.projection, t, formatMoney)
  : {})
const cartQuantity = computed(() => cartDisplayEvents.value.reduce(
  (total, cartEvent) => total + cartEvent.lines.reduce((eventTotal, line) => eventTotal + line.quantity, 0),
  0
))
const earliestExpiry = computed(() => cartDisplayEvents.value
  .map(cartEvent => cartEvent.expiresAt)
  .sort((left, right) => Date.parse(left) - Date.parse(right))[0])
// Confirms additions where the shopper is looking; the restored cart on load is not news.
const heldLines = computed(() => new Map(cartDisplayEvents.value.flatMap(cartEvent => (
  cartEvent.lines.map(line => [`${cartEvent.key}:${line.key}`, line] as const)
))))
watch(heldLines, (next, previous) => {
  if (loading.value || !compact.value) return
  const added = [...next].find(([key, line]) => line.quantity > (previous.get(key)?.quantity ?? 0))
  if (added) showAddedNotice(added[1].label)
})
const acceptedTotal = computed(() => purchaseSession.acceptedCart
  ? { amount: purchaseSession.acceptedCart.total, currency: purchaseSession.acceptedCart.currency ?? '' }
  : null)
const promotionBlocksCheckout = computed(() => {
  const promotion = purchaseSession.acceptedCart?.promotion
  return promotionLoading.value || Boolean(promotion && promotion.status !== 'applied')
})
const checkoutPath = computed(() => localePath('/checkout'))

function resolveSessionErrorMessage(value: unknown) {
  const code = value && typeof value === 'object' && 'code' in value
    ? String(value.code)
    : ''
  if (code === 'PUBLIC_MIN_PER_ORDER_NOT_MET') {
    const minimum = value && typeof value === 'object' && 'details' in value ? Number((value.details as Record<string, unknown>)?.minimum) : 1
    return t('public.purchase.quantityMinimumNotMet', { count: minimum || 1 })
  }
  if (code === 'PUBLIC_MAX_PER_ORDER_EXCEEDED') {
    const details = value && typeof value === 'object' && 'details' in value
      && value.details && typeof value.details === 'object'
      ? value.details as Record<string, unknown>
      : null
    const serverMaximum = Number(details?.maximum)
    if (Number.isInteger(serverMaximum) && serverMaximum > 0) {
      return t('public.purchase.quantityMaximumExceeded', { count: serverMaximum })
    }
    const exceededLocality = purchaseMap.value?.projection.localities.find((locality) => {
      if (locality.maxPerOrder === null) return false
      const requestedQuantity = selectedLines.value
        .filter(line => line.localityId === locality.id)
        .reduce((total, line) => total + line.quantity, 0)
      return requestedQuantity > locality.maxPerOrder
    })
    if (exceededLocality?.maxPerOrder !== null && exceededLocality?.maxPerOrder !== undefined) {
      return t('public.purchase.quantityMaximumExceeded', { count: exceededLocality.maxPerOrder })
    }
  }
  return resolveAppErrorMessage(value, t)
}

watch(sessionError, (value) => {
  if (!value) return
  toast.error(resolveSessionErrorMessage(value))
  restoreAcceptedSelection()
  void refreshAvailability()
}, { immediate: true })

function selectedLine(unitId?: string, localityId?: string) {
  return selectedLines.value.find(line => unitId ? line.unitId === unitId : line.localityId === localityId && !line.unitId)
}

function synchronize() {
  if (!event.value || !purchaseMap.value) return
  purchaseSession.synchronize({
    publicSlug: event.value.slug,
    eventId: event.value.id,
    eventName: event.value.name,
    occurrence: event.value.occurrence,
    mapVersion: purchaseMap.value.mapVersion
  }, selectedLines.value)
}

function toggleUnit(unitId: string) {
  const projection = purchaseMap.value?.projection
  if (!projection) return
  const existing = selectedLine(unitId)
  const unit = projection.units.find(item => item.id === unitId)
  if (!unit) return
  const locality = projection.localities.find(item => item.id === unit.localityId)
  if (!locality) return
  const currentQuantity = selectedLines.value.filter(line => line.localityId === locality.id).reduce((sum, line) => sum + line.quantity, 0)
  availabilityGeneration += 1
  if (existing) {
    const remaining = currentQuantity - existing.quantity
    selectedLines.value = selectedLines.value.filter(line => remaining > 0 && remaining < (locality.minPerOrder ?? 1) ? line.localityId !== locality.id : line !== existing)
  } else {
    try {
      selectedLines.value = [...selectedLines.value, ...createInitialPublicUnitCandidates(projection, selectedLines.value, unitId)]
    } catch {
      toast.error(t('public.purchase.minimumUnavailable'))
      return
    }
  }
  synchronize()
  // A tap both selects and pins the unit card; on phones the bar confirms instead of a card over the seats.
  if (compact.value) inspectedUnitId.value = null
  if (!existing && purchaseTourAwaitingSeat) {
    purchaseTourAwaitingSeat = false
    void nextTick(() => purchaseTour?.moveNext())
  }
}

function updateGeneralAdmission(localityId: string, quantity: number) {
  const projection = purchaseMap.value?.projection
  if (!projection) return
  const current = selectedLine(undefined, localityId)
  const locality = projection.localities.find(item => item.id === localityId)
  if (quantity > 0) quantity = Math.max(quantity, locality?.minPerOrder ?? 1)
  if (locality && quantity > Math.min(locality.availableQuantity, locality.maxPerOrder ?? Number.POSITIVE_INFINITY)) {
    toast.error(t('public.purchase.minimumUnavailable')); return
  }
  const candidate = quantity > 0 ? createPublicCartCandidate(projection, { localityId, quantity }) : null
  const next = candidate ? { ...candidate, ...(locality ? { lineLabel: locality.name, localityName: locality.name } : {}) } : null
  availabilityGeneration += 1
  selectedLines.value = selectedLines.value.filter(line => line !== current)
  if (next) selectedLines.value = [...selectedLines.value, next]
  synchronize()
  if (next && purchaseTourAwaitingSeat) {
    purchaseTourAwaitingSeat = false
    void nextTick(() => purchaseTour?.moveNext())
  }
}

function selectGeneralAdmissionLocality(localityId: string) {
  focusedLocalityId.value = localityId
  if (selectedLine(undefined, localityId)) return
  updateGeneralAdmission(localityId, purchaseMap.value?.projection.localities.find(locality => locality.id === localityId)?.minPerOrder ?? 1)
}

async function removeSelection(selection: { eventId: string; occurrenceId: string; lineKey: string }) {
  if (selection.eventId === event.value?.id && selection.occurrenceId === event.value.occurrence.id) {
    availabilityGeneration += 1
    selectedLines.value = selectedLines.value.filter(line => (line.unitId ?? `locality:${line.localityId}`) !== selection.lineKey)
    synchronize()
    return
  }
  await purchaseSession.removeLine(selection.eventId, selection.occurrenceId, selection.lineKey)
}

async function changeSelectionQuantity(selection: { eventId: string; occurrenceId: string; lineKey: string; quantity: number }) {
  await purchaseSession.updateLineQuantity(selection.eventId, selection.occurrenceId, selection.lineKey, selection.quantity)
  if (selection.eventId === event.value?.id && selection.occurrenceId === event.value.occurrence.id) {
    restoreAcceptedSelection()
  }
}

async function continueToCheckout() {
  await router.push(checkoutPath.value)
}

async function renewSelection(selection: { eventId: string; occurrenceId: string }) {
  await purchaseSession.renew(selection.eventId, selection.occurrenceId)
}

async function clearCart() {
  availabilityGeneration += 1
  selectedLines.value = []
  try {
    await purchaseSession.clear()
  } catch (clearError) {
    toast.error(resolveAppErrorMessage(clearError, t))
  }
  // Re-read the authoritative cart and map: a failed release must not look empty.
  void refreshAvailability()
}

function focusLocality(localityId: string | null) {
  focusedLocalityId.value = localityId
  if (localityId && purchaseTourAwaitingLocality) {
    purchaseTourAwaitingLocality = false
    void nextTick(() => purchaseTour?.moveNext())
  }
}

function inspectUnit(unitId: string | null) {
  inspectedUnitId.value = unitId
}
</script>

<template>
  <section class="public-event-purchase page-section public-page-shell" :class="{ 'public-event-purchase--with-bar': compact }">
    <SiteTopbar />
    <header v-if="event && showEventHeader" class="public-event-purchase__header" data-purchase-tour="event">
      <div>
        <NuxtLink class="public-event-purchase__back" :to="eventsPath" @click="backToEvents"><UIcon name="i-lucide-arrow-left" aria-hidden="true" />{{ $t('public.purchase.backToEvents') }}</NuxtLink>
        <div class="public-event-purchase__title"><h1>{{ event.name }}<small v-if="pulepCode" class="public-event-purchase__pulep"> · PULEP {{ pulepCode }}</small></h1>
        <PublicEventMediaPlayer kind="audio" v-if="promotionalAudio" :key="promotionalAudio" class="public-event-purchase__audio" :src="promotionalAudio" :label="$t('public.purchase.eventHelp.promoAudio')" />
        </div>
        <p>{{ event.venue?.name }} · {{ eventStartsAt }}<span v-if="organizers.length"> · {{ $t('public.purchase.eventHelp.organizers') }}: {{ organizers.join(' · ') }}</span></p>
      </div>
      <PublicPurchaseEventHelp :event="event" @organizers="organizers = $event" @pulep="pulepCode = $event" @audio="promotionalAudio = $event" @tour="startPurchaseTour" />
    </header>
    <template v-if="hydrated">
      <div v-if="loading" class="public-event-purchase__loading" role="status">{{ $t('accessibility.loading') }}</div>
      <PreregistrationAccessCard v-else-if="priorityAccess && !priorityAccess.access.canPurchase && !error" :value="priorityAccess" @refresh="load" />
      <TqEmptyState v-else-if="event && unavailableReason" :title="$t(`public.purchase.${unavailableReason}Title`)" :description="$t(`public.purchase.${unavailableReason}Description`)">
        <TqButton variant="secondary" :to="useLocalePath()('/events')">{{ $t('public.purchase.backToEvents') }}</TqButton>
      </TqEmptyState>
      <TqEmptyState v-else-if="error || !event || !purchaseMap" :title="$t('public.purchase.unavailableTitle')" :description="resolveAppErrorMessage(error, t)">
        <TqButton variant="secondary" :to="useLocalePath()('/events')">{{ $t('public.purchase.backToEvents') }}</TqButton>
      </TqEmptyState>
      <template v-else>
      <div class="public-event-purchase__workspace">
        <div class="public-event-purchase__map" data-purchase-tour="map">
          <RealtimeEventMapRenderer
            :document="purchaseMap.renderDocument"
            :load-export-snapshot="loadExportSnapshot"
            :realtime-context="mapRealtimeContext"
            :known-version="purchaseMap.contentVersion"
            :apply-patch="applyMapPatch"
            :focused-locality-id="focusedLocalityId"
            :inspected-unit-id="inspectedUnitId"
            :owned-unit-ids="acceptedUnitIds"
            :pending-unit-ids="pendingUnitIds"
            :owned-locality-ids="acceptedLocalityIds"
            :pending-locality-ids="pendingLocalityIds"
            :unit-details="unitDetails"
            :locality-captions="localityCaptions"
            :selectable="true"
            :refresh-availability="refreshAvailability"
            @focus-locality="focusLocality"
            @inspect-unit="inspectUnit"
            @select-unit="toggleUnit"
            @select-locality="selectGeneralAdmissionLocality"
          />
        </div>
        <aside v-if="!compact" class="public-event-purchase__cart" data-purchase-tour="cart" :aria-label="$t('public.purchase.selectionLabel')">
          <header class="public-event-purchase__cart-header">
            <UIcon name="i-lucide-shopping-cart" aria-hidden="true" />
            <h2>{{ $t('public.purchase.cartTitle') }}</h2>
          </header>
          <div class="public-event-purchase__cart-body">
            <div class="public-event-purchase__cart-scroll" tabindex="0" :aria-label="$t('public.purchase.selectionLabel')">
            <PublicPurchaseQuantities
              :localities="generalAdmissionLocalities"
              :quantities="generalAdmissionQuantities"
              @change="updateGeneralAdmission"
            />
              <PublicPurchaseCartLines
                :events="cartDisplayEvents"
                :synchronizing="synchronizing"
                @remove="removeSelection"
                @change-quantity="changeSelectionQuantity"
                @renew="renewSelection"
                @clear="clearCart"
              />
            </div>
          </div>
          <footer class="public-event-purchase__cart-footer">
            <PublicPurchasePromotionForm id="event-promotion-code" v-model:loading="promotionLoading" data-purchase-tour="promotion" />
            <PublicPurchaseCartSummary
              data-purchase-tour="checkout"
              :total="acceptedTotal"
              :disabled="!cartDisplayEvents.length || promotionBlocksCheckout"
              @continue="continueToCheckout"
            />
          </footer>
        </aside>
      </div>

      <template v-if="compact">
        <Transition name="public-event-purchase__bar">
          <PublicPurchaseBar
            v-if="!cartOpen"
            :quantity="cartQuantity"
            :total="acceptedTotal"
            :expires-at="earliestExpiry"
            :notice="addedNotice"
            @open="cartOpen = true"
          >
            <PublicPurchaseQuantities
              :localities="focusedGeneralAdmission"
              :quantities="generalAdmissionQuantities"
              @change="updateGeneralAdmission"
            />
          </PublicPurchaseBar>
        </Transition>
        <USlideover
          v-model:open="cartOpen"
          side="bottom"
          :title="$t('public.purchase.cartTitle')"
          :description="$t('public.purchase.cartDescription')"
          :ui="{ content: 'public-event-purchase__sheet', body: 'public-event-purchase__sheet-body', footer: 'public-event-purchase__sheet-footer' }"
          :modal="!purchaseTouring"
          :dismissible="!purchaseTouring"
        >
          <template #body>
            <PublicPurchaseQuantities
              :localities="generalAdmissionLocalities"
              :quantities="generalAdmissionQuantities"
              @change="updateGeneralAdmission"
            />
            <PublicPurchaseCartLines
              :events="cartDisplayEvents"
              :synchronizing="synchronizing"
              @remove="removeSelection"
              @change-quantity="changeSelectionQuantity"
              @renew="renewSelection"
              @clear="clearCart"
            />
          </template>
          <template #footer>
            <PublicPurchasePromotionForm id="event-promotion-code" v-model:loading="promotionLoading" data-purchase-tour="promotion" />
            <PublicPurchaseCartSummary
              data-purchase-tour="checkout"
              :total="acceptedTotal"
              :disabled="!cartDisplayEvents.length || promotionBlocksCheckout"
              @continue="continueToCheckout"
            />
          </template>
        </USlideover>
      </template>
      </template>
    </template>
  </section>
</template>

<style scoped>
.public-event-purchase__title { display: flex; align-items: center; flex-wrap: wrap; gap: .75rem; }
.public-event-purchase__audio { width: 230px; height: 32px; max-width: 100%; }
.public-event-purchase__pulep { font-size: .65em; color: var(--tq-text-soft); }
/* overflow:clip makes this the sticky topbar's scroll container (as the landing page already does), so its "top" offset isn't wrongly added to its initial position; padding-top matches the landing page's top spacing */
.public-event-purchase { display: flex; flex-direction: column; min-height: 100vh; gap: 1rem; padding-top: clamp(0.55rem, 1.6vw, 1.5rem); overflow: clip; }
.public-event-purchase :deep(.public-topbar) { flex-shrink: 0; width: 100%; max-width: none; }
.public-event-purchase__header { flex-shrink: 0; display: flex; gap: 1rem; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; }
.public-event-purchase__header h1, .public-event-purchase__header p { margin: 0; }
.public-event-purchase__header > div:not(.purchase-help-actions) { display: grid; grid-template-columns: minmax(0, 1fr); min-width: 0; max-width: 100%; gap: .35rem; overflow-wrap: anywhere; }
.public-event-purchase__header h1 { font-size: 1.4rem; }
.public-event-purchase__header p { color: var(--tq-text-soft); font-size: .9rem; }
.public-event-purchase__back { display: inline-flex; align-items: center; gap: .35rem; justify-self: start; min-height: 2rem; margin-inline-start: -.35rem; border-radius: var(--tq-radius-pill); padding: .2rem .55rem .2rem .35rem; color: var(--tq-accent-readable); font-size: .8rem; font-weight: 700; text-decoration: none; transition: background 160ms var(--tq-ease-out); }
.public-event-purchase__back:hover { background: color-mix(in oklch, var(--tq-accent-readable) 10%, transparent); }
.public-event-purchase__back:focus-visible { outline: 2px solid var(--tq-focus); outline-offset: 2px; }
.public-event-purchase__back .iconify { width: 1rem; height: 1rem; transition: transform 160ms var(--tq-ease-out); }
.public-event-purchase__back:hover .iconify { transform: translateX(-2px); }
.public-event-purchase__workspace { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr) minmax(18rem, 24rem); gap: 1rem; align-items: stretch; }
.public-event-purchase__map { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.public-event-purchase__map :deep(.event-map-renderer) { flex: 1; min-height: 0; }
.public-event-purchase__cart {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
  box-shadow: var(--tq-shadow-tight);
}
.public-event-purchase__cart-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: .55rem;
  padding: .85rem 1rem;
  border-bottom: 1px solid var(--tq-border);
  background: color-mix(in oklch, var(--tq-bg-elevated) 88%, var(--tq-bg-muted));
}
.public-event-purchase__cart-header h2 { margin: 0; font-size: 1rem; }
.public-event-purchase__cart-header :deep(.iconify) { width: 1.1rem; height: 1.1rem; color: var(--tq-teal-readable); }
.public-event-purchase__cart-body { display: flex; flex-direction: column; flex: 1; gap: .75rem; min-height: 0; padding: .85rem 1rem; }
.public-event-purchase__cart-scroll > * + * { margin-top: .65rem; }
.public-event-purchase__cart-scroll { flex: 1; min-height: 0; overflow-y: scroll; scrollbar-gutter: stable; scrollbar-width: auto; scrollbar-color: var(--tq-text-muted) var(--tq-bg-muted); padding-right: .4rem; }
.public-event-purchase__cart-scroll::-webkit-scrollbar { width: 9px; }
.public-event-purchase__cart-scroll::-webkit-scrollbar-track { background: var(--tq-bg-muted); }
.public-event-purchase__cart-scroll::-webkit-scrollbar-thumb { background: var(--tq-text-muted); border-radius: var(--tq-radius-sm); }
.public-event-purchase__cart-footer {
  display: grid;
  flex-shrink: 0;
  gap: .5rem;
  padding: .65rem .85rem;
  border-top: 1px solid var(--tq-border);
  background: color-mix(in oklch, var(--tq-bg-elevated) 88%, var(--tq-bg-muted));
}
.public-event-purchase__loading { display: grid; min-height: 20rem; place-items: center; }
@media (min-width: 901px) {
  .public-event-purchase { height: 100vh; overflow: clip; }
}
@media (max-width: 900px) {
  .public-event-purchase { height: auto; overflow: visible; }
  .public-event-purchase__workspace { grid-template-columns: 1fr; }
  .public-event-purchase__map :deep(.event-map-renderer) { flex: none; }
  /* The map is the purchase surface on phones: give it most of the screen. */
  .public-event-purchase__map :deep(.event-map-renderer-client) { min-height: min(64dvh, 36rem); }
}
.public-event-purchase--with-bar { padding-bottom: calc(5.5rem + env(safe-area-inset-bottom)); }
.public-event-purchase__bar-enter-active, .public-event-purchase__bar-leave-active { transition: transform .25s var(--tq-ease-soft); }
.public-event-purchase__bar-enter-from, .public-event-purchase__bar-leave-to { transform: translateY(100%); }
@media (prefers-reduced-motion: reduce) {
  .public-event-purchase__bar-enter-active, .public-event-purchase__bar-leave-active { transition: none; }
}
</style>

<style>
.driver-active .driver-overlay * { pointer-events: none !important; }
/* The cart sheet is teleported, so its slot classes need unscoped rules. */
/* A fixed height keeps the sheet's top edge still while lines are added or removed. */
.public-event-purchase__sheet { height: min(88dvh, 46rem); max-height: none; border-radius: var(--tq-radius-lg) var(--tq-radius-lg) 0 0; }
.public-event-purchase__sheet-body { display: grid; gap: 1.25rem; align-content: start; background: var(--tq-bg-muted); }
.public-event-purchase__sheet-footer { display: grid; gap: .75rem; padding-bottom: calc(1rem + env(safe-area-inset-bottom)); border-top: 1px solid var(--tq-border); background: var(--tq-bg-elevated); }
.driver-popover.tq-purchase-tour {
  max-width: min(25rem, calc(100vw - 2rem));
  padding: 1.1rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-popover-bg);
  color: var(--tq-text);
  box-shadow: var(--tq-shadow-soft);
}
.driver-popover.tq-purchase-tour .driver-popover-title {
  padding-right: 1.75rem;
  color: var(--tq-text-heading);
  font-size: 1.05rem;
}
.driver-popover.tq-purchase-tour .driver-popover-description {
  color: var(--tq-text-muted);
  font-size: .9rem;
  line-height: 1.6;
}
.driver-popover.tq-purchase-tour .driver-popover-progress-text,
.driver-popover.tq-purchase-tour .driver-popover-close-btn {
  color: var(--tq-text-soft);
}
.driver-popover.tq-purchase-tour .driver-popover-footer-btn {
  padding: .45rem .7rem;
  border: 1px solid var(--tq-secondary-action-border);
  border-radius: var(--tq-radius-md);
  background: var(--tq-secondary-action);
  color: var(--tq-on-secondary-action);
  font: inherit;
  font-size: .8rem;
  font-weight: 700;
}
.driver-popover.tq-purchase-tour .driver-popover-next-btn {
  border-color: var(--tq-action-border);
  background: var(--tq-action);
  color: var(--tq-on-action);
}
.driver-popover.tq-purchase-tour .driver-popover-footer-btn:focus-visible,
.driver-popover.tq-purchase-tour .driver-popover-close-btn:focus-visible {
  outline: 2px solid var(--tq-focus);
  outline-offset: 2px;
}
.driver-popover.tq-purchase-tour .driver-popover-arrow-side-left { border-left-color: var(--tq-popover-bg); }
.driver-popover.tq-purchase-tour .driver-popover-arrow-side-right { border-right-color: var(--tq-popover-bg); }
.driver-popover.tq-purchase-tour .driver-popover-arrow-side-top { border-top-color: var(--tq-popover-bg); }
.driver-popover.tq-purchase-tour .driver-popover-arrow-side-bottom { border-bottom-color: var(--tq-popover-bg); }
</style>
